import { muzakereApi } from "./muzakereApi";
import {
  decideBody,
  listBody,
  normalizeAccessRequest,
  normalizeDiff,
  normalizePackage,
  normalizeProposal,
  replyPayload,
  sortProposals,
} from "../app/library/libraryRules";

/**
 * Müzakere kütüphanesi (mobil uçlar /api/muzakere-library/*, yalnız JWT;
 * imza istemez). Ayrı dilim açmamak için müzakere diliminin içine eklenir;
 * store'a ayrıca kaydedilmez. Erişim (en az bir grupta yönetici) sunucuda
 * denetlenir.
 */
const url = (path) => `/api/muzakere-library${path}`;
const post = (path, data) => ({ url: url(path), method: "POST", data });

const PKG = "LibraryPackage";
const PROPOSALS = "LibraryProposals";
const REQUESTS = "LibraryRequests";

export const libraryApi = muzakereApi.enhanceEndpoints({ addTagTypes: [PKG, PROPOSALS, REQUESTS] }).injectEndpoints({
  endpoints: (builder) => ({
    // Menü ve düğmeler için hafif erişim bilgisi: { canUse }
    getLibraryAccess: builder.query({
      query: () => post("/access", {}),
    }),
    // tab: "mine" | "starred" | "explore"; sayfa 20'şer, hasMore döner.
    // Yanıt ham biçimde (data.data.packages); sayfalar normalizePackage uygular.
    listPackages: builder.query({
      query: (args) => post("/list", listBody(args)),
      providesTags: ["Library"],
    }),
    // Paketin soruları; kilitliyse sorular gelmez, locked döner.
    getPackage: builder.mutation({
      query: ({ packageId }) => post("/get", { packageId }),
    }),
    // Paket sayfası: önbellekli ve normalize edilmiş paket.
    packageDetail: builder.query({
      query: ({ packageId }) => post("/get", { packageId }),
      transformResponse: (r) => normalizePackage(r?.data?.package) || null,
      providesTags: (result, error, { packageId }) => [{ type: PKG, id: packageId }],
    }),
    packageForks: builder.query({
      query: ({ packageId }) => post("/forks", { packageId }),
      transformResponse: (r) => (r?.data?.forks || []).map(normalizePackage),
      providesTags: (result, error, { packageId }) => [{ type: PKG, id: packageId }, "Library"],
    }),
    // Yalnız sahip: yıldızlayanlar (yeniden eskiye).
    packageStargazers: builder.query({
      query: ({ packageId }) => post("/stargazers", { packageId }),
      transformResponse: (r) => r?.data?.users || [],
      providesTags: (result, error, { packageId }) => [{ type: PKG, id: packageId }],
    }),
    // Sahip: pakete gelen ve (çatalsa) bu çataldan giden öneriler; açıklar önce.
    packageProposals: builder.query({
      query: ({ packageId, status = "all" }) => post("/proposals/list", { packageId, status }),
      transformResponse: (r) => sortProposals((r?.data?.proposals || []).map(normalizeProposal)),
      providesTags: [PROPOSALS],
    }),
    // Öneri ve farkı; bakana kilitliyse fark gelmez (diff null).
    proposal: builder.query({
      query: ({ proposalId }) => post("/proposals/get", { proposalId }),
      transformResponse: (r) => ({
        proposal: normalizeProposal(r?.data?.proposal),
        diff: r?.data?.diff ? normalizeDiff(r.data.diff) : null,
      }),
      providesTags: (result, error, { proposalId }) => [{ type: PROPOSALS, id: proposalId }, PROPOSALS],
    }),
    // role: "incoming" | "outgoing"
    accessRequests: builder.query({
      query: ({ role }) => post("/access-requests/list", { role }),
      transformResponse: (r) => (r?.data?.requests || []).map(normalizeAccessRequest),
      providesTags: [REQUESTS],
    }),
    createPackage: builder.mutation({
      query: ({ title, description, tags, questions, submit }) =>
        post("/create", {
          title,
          questions,
          ...(description ? { description } : {}),
          ...(tags ? { tags } : {}),
          ...(submit ? { submit: true } : {}),
        }),
      invalidatesTags: ["Library"],
    }),
    // Yalnız sahip; gelmeyen alan değişmez.
    updatePackage: builder.mutation({
      query: ({ packageId, ...patch }) => post("/update", { packageId, ...patch }),
      invalidatesTags: (result, error, { packageId }) => ["Library", PROPOSALS, { type: PKG, id: packageId }],
    }),
    deletePackage: builder.mutation({
      query: ({ packageId }) => post("/delete", { packageId }),
      invalidatesTags: ["Library", PROPOSALS],
    }),
    starPackage: builder.mutation({
      query: ({ packageId, starred }) => post("/star", { packageId, starred }),
      invalidatesTags: (result, error, { packageId }) => ["Library", { type: PKG, id: packageId }],
    }),
    forkPackage: builder.mutation({
      query: ({ packageId }) => post("/fork", { packageId }),
      invalidatesTags: (result, error, { packageId }) => ["Library", { type: PKG, id: packageId }],
    }),
    reportPackage: builder.mutation({
      query: ({ packageId }) => post("/report", { packageId }),
    }),
    // Sahip: onaya gönder (true) ya da özele çevir (false: geri çek, yayından kaldır).
    submitPackage: builder.mutation({
      query: ({ packageId, submitted }) => post("/submit", { packageId, submitted }),
      invalidatesTags: (result, error, { packageId }) => ["Library", { type: PKG, id: packageId }],
    }),
    // Diğer paketlerin sorularında arama (30'ar, hasMore).
    searchQuestions: builder.mutation({
      query: ({ q, type, page }) => post("/search-questions", { q, ...(type ? { type } : {}), ...(page ? { page } : {}) }),
    }),
    createProposal: builder.mutation({
      query: ({ forkId, message }) => {
        const text = replyPayload(message, 500);
        return post("/proposals/create", { forkId, ...(text ? { message: text } : {}) });
      },
      invalidatesTags: ["Library", PROPOSALS],
    }),
    // Gövde: libraryRules.decideBody (cevap ve çakışma seçimleri).
    decideProposal: builder.mutation({
      query: (args) => post("/proposals/decide", decideBody(args)),
      invalidatesTags: ["Library", PROPOSALS, PKG],
    }),
    withdrawProposal: builder.mutation({
      query: ({ proposalId }) => post("/proposals/withdraw", { proposalId }),
      invalidatesTags: ["Library", PROPOSALS],
    }),
    // Kilitli paketi görmek için izin isteği.
    createAccessRequest: builder.mutation({
      query: ({ packageId }) => post("/access-requests/create", { packageId }),
      invalidatesTags: ["Library", REQUESTS],
    }),
    decideAccessRequest: builder.mutation({
      query: ({ requestId, action, message }) => {
        const text = replyPayload(message);
        return post("/access-requests/decide", { requestId, action, ...(text ? { message: text } : {}) });
      },
      invalidatesTags: [REQUESTS],
    }),
  }),
});

export const {
  useGetLibraryAccessQuery,
  useListPackagesQuery,
  useGetPackageMutation,
  usePackageDetailQuery,
  usePackageForksQuery,
  usePackageStargazersQuery,
  usePackageProposalsQuery,
  useProposalQuery,
  useAccessRequestsQuery,
  useCreatePackageMutation,
  useUpdatePackageMutation,
  useDeletePackageMutation,
  useStarPackageMutation,
  useForkPackageMutation,
  useReportPackageMutation,
  useSubmitPackageMutation,
  useSearchQuestionsMutation,
  useCreateProposalMutation,
  useDecideProposalMutation,
  useWithdrawProposalMutation,
  useCreateAccessRequestMutation,
  useDecideAccessRequestMutation,
} = libraryApi;
